import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  StarIcon,
  CalendarIcon,
  CheckBadgeIcon,
  ArrowLeftIcon,
  LockClosedIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import SEO from '@components/common/SEO'
import { useCompany, useCompanyOwnership } from '@/hooks/useApi'
import { Button } from '@components/ui/Button'
import { ApiService, API_URL } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'
import ClaimCompanyModal from '@/components/ClaimCompanyModal'
import StaticMap from '@/components/maps/StaticMap'
import ImageGallery from '@/components/company/ImageGallery'
import { normalizeWebsiteUrl } from '@/utils/urlUtils'

const CompanyDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const { data: company, isLoading, error } = useCompany(slug || '')
  const { user, isAuthenticated, isAdmin, isMember, isGuest } = useAuth()
  const navigate = useNavigate()
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false)

  // Get navigation context from URL parameters
  const fromPage = searchParams.get('from') // 'search' or null
  const viewMode = searchParams.get('view') || 'list' // 'map' or 'list'

  // Preserve all other URL parameters when navigating back to search
  // This ensures filters (province, serviceType, companySize, etc.) are maintained
  const getBackToSearchUrl = () => {
    // Create a new URLSearchParams from current search params (which were passed from search page)
    const backParams = new URLSearchParams(searchParams)

    // Remove navigation-specific params (from, view are redundant)
    backParams.delete('from')

    // Set view mode
    backParams.set('view', viewMode)

    return `/search?${backParams.toString()}`
  }

  // Check company ownership status (none/pending/approved)
  // CRITICAL FIX: Pass isAuthenticated to prevent GUEST users from triggering 401 errors
  const { data: ownershipStatus, isLoading: isCheckingOwnership } = useCompanyOwnership(company?.id, isAuthenticated)

  // Determine if user can see contact information
  // ADMIN and MEMBER can see all contact info (ownership is for EDITING, not VIEWING)
  // GUEST cannot see any contact info
  const canSeeContact = isAdmin || isMember

  // Track page view when company data is loaded
  useEffect(() => {
    if (company && !isLoading && !error) {
      ApiService.trackPageView(company.id, window.location.pathname)
    }
  }, [company, isLoading, error])

  // Handle claim button click
  const handleClaimClick = () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    // Open claim modal
    setIsClaimModalOpen(true)
  }

  // Handle successful claim submission
  const handleClaimSuccess = () => {
    // Redirect to my-claims page after successful claim
    navigate('/my-claims')
  }

  // Helper functions
  const getCompanySizeText = (size: string) => {
    switch (size) {
      case 'SMALL': return 'เล็ก'
      case 'MEDIUM': return 'กลาง'
      case 'LARGE': return 'ใหญ่'
      default: return ''
    }
  }

  const formatPhoneNumber = (phone: string | null | undefined) => {
    // Handle null, undefined, or empty phone numbers
    if (!phone) return ''

    // Ensure it's a string
    if (typeof phone !== 'string') return ''

    // Format phone number for display
    return phone.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3')
  }

  // Helper function to convert relative path to full URL
  const getImageUrl = (url: string | null | undefined): string | null => {
    if (!url) return null
    // If already a full URL, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    // Convert relative path to full URL
    return `${API_URL}${url}`
  }

  // Helper function to track contact interactions
  const handleContactClick = (
    contactType: 'phone' | 'email' | 'website' | 'line' | 'facebook',
    contactValue: string,
    action: () => void
  ) => {
    if (company) {
      ApiService.trackContact(company.id, contactType, contactValue)
    }
    action()
  }

  // Handle error state (404 or other errors)
  if (error) {
    return (
      <>
        <SEO title="404 - ไม่พบบริษัทที่ค้นหา" description="ไม่พบข้อมูลบริษัทรักษาความปลอดภัยที่ต้องการ" />
        <div className="container-wrapper py-12">
          <div className="max-w-2xl mx-auto text-center">
            {/* 404 Icon */}
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-red-50 rounded-full mb-6">
                <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold text-neutral-900 mb-3">ไม่พบบริษัทที่ค้นหา</h1>
              <p className="text-lg text-neutral-600 mb-8">
                ขออภัย เราไม่พบบริษัทรักษาความปลอดภัยที่คุณต้องการ
              </p>
            </div>

            {/* Error Details */}
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-semibold text-neutral-900 mb-3">สาเหตุที่อาจเป็นไปได้:</h3>
              <ul className="space-y-2 text-neutral-600">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-neutral-400 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <circle cx="10" cy="10" r="2"/>
                  </svg>
                  <span>ลิงค์ที่คุณเข้าถึงอาจไม่ถูกต้องหรือเปลี่ยนแปลงไปแล้ว</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-neutral-400 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <circle cx="10" cy="10" r="2"/>
                  </svg>
                  <span>บริษัทนี้อาจถูกลบหรือย้ายออกจากระบบชั่วคราว</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-neutral-400 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <circle cx="10" cy="10" r="2"/>
                  </svg>
                  <span>URL อาจถูกพิมพ์ผิด กรุณาตรวจสอบอีกครั้ง</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/search">
                <Button
                  variant="default"
                  size="lg"
                  className="w-full sm:w-auto"
                  leftIcon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  }
                >
                  ค้นหาบริษัทอื่น
                </Button>
              </Link>
              <Link to="/">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto"
                  leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
                >
                  กลับหน้าแรก
                </Button>
              </Link>
            </div>

            {/* Help Text */}
            <div className="mt-8 pt-8 border-t border-neutral-200">
              <p className="text-sm text-neutral-500">
                หากคุณเชื่อว่านี่เป็นข้อผิดพลาด กรุณา{' '}
                <a
                  href="mailto:info@koder3.com?subject=รายงานปัญหา 404"
                  className="text-primary-600 hover:text-primary-700 font-medium underline"
                >
                  แจ้งทีมงาน
                </a>
                {' '}เพื่อให้เราช่วยแก้ไขปัญหา
              </p>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (isLoading) {
    return (
      <>
        <SEO title="กำลังโหลด..." description="กำลังโหลดข้อมูลบริษัท" />
        <div className="container-wrapper py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-neutral-200 rounded w-1/3 mb-6"></div>
            <div className="space-y-4">
              <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
              <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
              <div className="h-4 bg-neutral-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // If no company data and no error (shouldn't happen, but handle gracefully)
  if (!company) {
    return (
      <>
        <SEO title="ไม่พบข้อมูล" description="ไม่มีข้อมูลบริษัท" />
        <div className="container-wrapper py-12">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-2xl font-bold text-neutral-900 mb-4">ไม่มีข้อมูลบริษัท</h1>
            <p className="text-neutral-600 mb-6">ขออภัย ไม่สามารถแสดงข้อมูลบริษัทได้ในขณะนี้</p>
            <Link to="/search">
              <Button variant="default">กลับไปค้นหา</Button>
            </Link>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SEO
        title={company.nameTh}
        description={company.description}
      />

      {/* Claim Company Modal */}
      <ClaimCompanyModal
        isOpen={isClaimModalOpen}
        onClose={() => setIsClaimModalOpen(false)}
        companyId={company.id}
        companyName={company.nameTh}
        onSuccess={handleClaimSuccess}
      />

      <div className="container-wrapper py-8">
        {/* Back Button */}
        {fromPage === 'search' ? (
          <Link
            to={getBackToSearchUrl()}
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            กลับไปยังผลการค้นหา
          </Link>
        ) : (
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            ย้อนกลับ
          </button>
        )}

        {/* Company Header */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-sm mb-6">
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Side - Company Information */}
              <div className="lg:col-span-2">
                {/* Company Logo and Name */}
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-neutral-100 rounded-lg flex items-center justify-center overflow-hidden">
                      {getImageUrl(company.logoUrl) ? (
                        <img
                          src={getImageUrl(company.logoUrl)!}
                          alt={company.nameTh}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to default if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<svg class="w-10 h-10 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>';
                            }
                          }}
                        />
                      ) : (
                        <BuildingOfficeIcon className="w-10 h-10 text-neutral-600" />
                      )}
                    </div>
                  </div>

                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-neutral-900 mb-2">
                      {company.nameTh}
                    </h1>
                    {company.nameEn && (
                      <p className="text-lg text-neutral-600 mb-4">
                        {company.nameEn}
                      </p>
                    )}

                    {/* Status Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {company.isFeatured && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-amber-100 text-amber-800">
                          <StarIcon className="w-4 h-4 mr-1" />
                          บริษัทแนะนำ
                        </span>
                      )}
                      {company.isVerified && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-green-100 text-green-800">
                          <CheckBadgeIcon className="w-4 h-4 mr-1" />
                          ยืนยันแล้ว
                        </span>
                      )}
                      <span className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-blue-100 text-blue-800">
                        บริษัทขนาด{getCompanySizeText(company.companySize)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Company Description */}
                <div className="mb-6">
                  <p className="text-neutral-700">
                    {company.description}
                  </p>
                </div>

                {/* Quick Info */}
                <div className="space-y-2">
                  <p className="text-sm text-neutral-600 flex items-center">
                    <MapPinIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                    {company.province}, {company.district}
                  </p>
                  {company.establishedYear && (
                    <p className="text-sm text-neutral-600 flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                      ก่อตั้งปี {company.establishedYear}
                    </p>
                  )}
                  <p className="text-sm text-neutral-600">
                    ผู้เข้าชม: {company.viewCount.toLocaleString()} ครั้ง
                  </p>
                </div>
              </div>

              {/* Right Side - Contact Actions */}
              <div className="lg:col-span-1">
                <div className="bg-white border border-neutral-200 rounded-lg p-5 sticky top-6">
                  <h3 className="text-lg font-medium text-neutral-900 mb-5">ติดต่อเราเลย</h3>

                  {/* Show contact info if user has permission */}
                  {canSeeContact && company.phone1 ? (
                    <div className="space-y-3">
                      {/* Primary Phone Button */}
                      <button
                        onClick={() => handleContactClick(
                          'phone',
                          company.phone1,
                          () => window.open(`tel:${company.phone1}`, '_self')
                        )}
                        className="w-full flex items-center justify-center px-4 py-3 border-2 border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white rounded-md font-medium transition-all duration-200"
                      >
                        <PhoneIcon className="w-4 h-4 mr-2" />
                        โทร {formatPhoneNumber(company.phone1)}
                      </button>

                    {/* Secondary Contact Options */}
                    <div className="grid grid-cols-2 gap-2">
                      {canSeeContact && company.email && (
                        <button
                          onClick={() => handleContactClick(
                            'email',
                            company.email,
                            () => window.open(`mailto:${company.email}`, '_self')
                          )}
                          className="flex flex-col items-center justify-center px-3 py-3 border border-neutral-300 text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50 rounded-md font-normal transition-all duration-200"
                        >
                          <EnvelopeIcon className="w-4 h-4 mb-1" />
                          <span className="text-sm">อีเมล</span>
                        </button>
                      )}

                      {canSeeContact && company.lineId && (
                        <button
                          onClick={() => handleContactClick(
                            'line',
                            company.lineId,
                            () => window.open(`https://line.me/ti/p/${company.lineId}`, '_blank')
                          )}
                          className="flex flex-col items-center justify-center px-3 py-3 border border-neutral-300 text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50 rounded-md font-normal transition-all duration-200"
                        >
                          <svg className="w-4 h-4 mb-1" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.628-.629.628M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                          </svg>
                          <span className="text-sm">Line</span>
                        </button>
                      )}

                      {canSeeContact && company.facebookUrl && (
                        <button
                          onClick={() => handleContactClick(
                            'facebook',
                            company.facebookUrl,
                            () => window.open(company.facebookUrl, '_blank')
                          )}
                          className="flex flex-col items-center justify-center px-3 py-3 border border-neutral-300 text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50 rounded-md font-normal transition-all duration-200"
                        >
                          <svg className="w-4 h-4 mb-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                          <span className="text-sm">Facebook</span>
                        </button>
                      )}
                    </div>

                    {/* Additional Contact Buttons for remaining contacts */}
                    {(company.phone2 || company.phone3) && (
                      <div className="pt-3 border-t border-neutral-200 space-y-2">
                        {company.phone2 && (
                          <button
                            onClick={() => handleContactClick(
                              'phone',
                              company.phone2,
                              () => window.open(`tel:${company.phone2}`, '_self')
                            )}
                            className="w-full flex items-center justify-center px-4 py-2 border border-neutral-300 text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50 rounded-md font-normal transition-all duration-200"
                          >
                            <PhoneIcon className="w-4 h-4 mr-2" />
                            โทร {formatPhoneNumber(company.phone2)}
                          </button>
                        )}
                        {company.phone3 && (
                          <button
                            onClick={() => handleContactClick(
                              'phone',
                              company.phone3,
                              () => window.open(`tel:${company.phone3}`, '_self')
                            )}
                            className="w-full flex items-center justify-center px-4 py-2 border border-neutral-300 text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50 rounded-md font-normal transition-all duration-200"
                          >
                            <PhoneIcon className="w-4 h-4 mr-2" />
                            โทร {formatPhoneNumber(company.phone3)}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Claim Ownership Section - For MEMBER who haven't claimed yet */}
                    {isMember && !isAdmin && !isCheckingOwnership && (
                      <div className="pt-4 mt-4 border-t border-neutral-200">
                        {ownershipStatus?.status === 'approved' && ownershipStatus?.isOwner === true ? (
                          /* User already owns this company - Show Management Button */
                          <div className="space-y-3">
                            <div className="flex items-center justify-center text-green-600 bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                              <CheckBadgeIcon className="w-5 h-5 mr-2" />
                              <span className="font-medium text-sm">คุณเป็นเจ้าของบริษัทนี้</span>
                            </div>
                            <Button
                              variant="default"
                              size="default"
                              className="w-full"
                              onClick={() => navigate('/my-companies')}
                              leftIcon={
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              }
                            >
                              จัดการบริษัทของคุณ
                            </Button>
                          </div>
                        ) : ownershipStatus?.status === 'pending' && ownershipStatus?.isOwner === true ? (
                          /* Claim pending approval */
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                            <div className="flex items-center justify-center text-yellow-700 mb-2">
                              <ClockIcon className="w-5 h-5 mr-2" />
                              <span className="font-medium">รอการอนุมัติ</span>
                            </div>
                            <p className="text-xs text-yellow-600">
                              คำขอของคุณอยู่ระหว่างรอผู้ดูแลระบบอนุมัติ
                            </p>
                          </div>
                        ) : (
                          /* User can claim this company */
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-900 mb-3 text-center">
                              คุณเป็นเจ้าของบริษัทนี้หรือไม่?
                            </p>
                            <Button
                              variant="default"
                              size="default"
                              className="w-full"
                              onClick={handleClaimClick}
                              leftIcon={<BuildingOfficeIcon className="w-4 h-4" />}
                            >
                              ขอสิทธิ์เป็นเจ้าของบริษัท
                            </Button>
                            <p className="text-xs text-blue-600 mt-2 text-center">
                              หลังจากได้รับการอนุมัติ คุณจะสามารถแก้ไขข้อมูลบริษัทได้
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : isCheckingOwnership ? (
                  /* Loading ownership status */
                  <div className="space-y-4">
                    <div className="bg-neutral-50 border-2 border-neutral-200 rounded-lg p-6 text-center">
                      <div className="animate-pulse">
                        <div className="h-4 bg-neutral-200 rounded w-3/4 mx-auto mb-3"></div>
                        <div className="h-4 bg-neutral-200 rounded w-1/2 mx-auto"></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Contact info locked - Show login/claim message */
                  <div className="space-y-4">
                    <div className="bg-neutral-50 border-2 border-neutral-200 rounded-lg p-6 text-center">
                      <div className="flex justify-center mb-3">
                        <div className="w-12 h-12 bg-neutral-200 rounded-full flex items-center justify-center">
                          <LockClosedIcon className="w-6 h-6 text-neutral-600" />
                        </div>
                      </div>

                      <h4 className="text-base font-semibold text-neutral-900 mb-2">
                        ข้อมูลติดต่อถูกซ่อน
                      </h4>

                      {isGuest ? (
                        <div>
                          <p className="text-sm text-neutral-600 mb-4">
                            กรุณาเข้าสู่ระบบเพื่อดูข้อมูลติดต่อบริษัท
                          </p>
                          <Button
                            variant="default"
                            size="default"
                            className="w-full"
                            onClick={() => navigate('/login')}
                          >
                            เข้าสู่ระบบ
                          </Button>
                          <p className="text-xs text-neutral-500 mt-3">
                            ยังไม่มีบัญชี?{' '}
                            <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
                              สมัครสมาชิก
                            </Link>
                          </p>
                        </div>
                      ) : ownershipStatus?.status === 'pending' ? (
                        /* PENDING CLAIM - Show "รอการอนุมัติ" badge */
                        <div>
                          <p className="text-sm text-neutral-600 mb-4">
                            คำขอ claim ของคุณอยู่ระหว่างรอการอนุมัติ
                          </p>
                          <div className="inline-flex items-center px-4 py-2 bg-yellow-50 border-2 border-yellow-300 text-yellow-800 rounded-lg font-medium">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            รอการอนุมัติ
                          </div>
                          <p className="text-xs text-neutral-500 mt-3">
                            กรุณารอผู้ดูแลระบบอนุมัติคำขอของคุณ
                          </p>
                        </div>
                      ) : (
                        /* NO CLAIM - Show "Claim บริษัทนี้" button */
                        <div>
                          <p className="text-sm text-neutral-600 mb-4">
                            คุณต้องเป็นเจ้าของบริษัทนี้เพื่อดูข้อมูลติดต่อ
                          </p>
                          <Button
                            variant="default"
                            size="default"
                            className="w-full"
                            onClick={handleClaimClick}
                          >
                            Claim บริษัทนี้
                          </Button>
                          <p className="text-xs text-neutral-500 mt-3">
                            หลังจาก claim สำเร็จ คุณจะสามารถดูและแก้ไขข้อมูลได้
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Services */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
              <div className="border-b border-neutral-200 px-6 py-4">
                <h2 className="text-lg font-semibold text-neutral-900">บริการที่ให้</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {company.serviceTypes.map((service, index) => (
                    <button
                      key={index}
                      onClick={() => navigate(`/search?serviceType=${encodeURIComponent(service)}`)}
                      className="flex items-center p-2 bg-neutral-50 rounded cursor-pointer hover:bg-primary-50 hover:border-primary-200 border border-transparent transition-all duration-200 text-left group"
                    >
                      <div className="w-2 h-2 bg-primary-500 rounded-full mr-3 group-hover:bg-primary-600"></div>
                      <span className="text-neutral-700 group-hover:text-primary-700 group-hover:font-medium">{service}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Gallery Images Section */}
            {company.galleryImages && company.galleryImages.length > 0 && (
              <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
                <div className="border-b border-neutral-200 px-6 py-4">
                  <h2 className="text-lg font-semibold text-neutral-900">รูปภาพบริษัท</h2>
                </div>
                <div className="p-6">
                  <ImageGallery images={company.galleryImages} />
                </div>
              </div>
            )}

            {/* Service Areas */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
              <div className="border-b border-neutral-200 px-6 py-4">
                <h2 className="text-lg font-semibold text-neutral-900">พื้นที่ให้บริการ</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {company.serviceAreas.map((area, index) => (
                    <div key={index} className="flex items-center p-2 bg-neutral-50 rounded">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-neutral-700 text-sm">{area}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
              <div className="border-b border-neutral-200 px-6 py-4">
                <h2 className="text-lg font-semibold text-neutral-900">ข้อมูลติดต่อ</h2>
              </div>
              <div className="p-6 space-y-4">
                {/* Address */}
                <div className="p-4 bg-neutral-50 rounded">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-neutral-200 rounded flex items-center justify-center mr-3">
                      <MapPinIcon className="w-4 h-4 text-neutral-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-neutral-900 mb-1">ที่อยู่</h4>
                      <p className="text-neutral-700 text-sm">{company.address}</p>
                      <p className="text-neutral-700 text-sm">{company.district} {company.province} {company.postalCode}</p>
                    </div>
                  </div>
                </div>

                {/* Phone Numbers */}
                {canSeeContact ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {company.phone1 && (
                      <div className="p-4 bg-neutral-50 rounded">
                        <div className="flex items-center mb-2">
                          <div className="w-6 h-6 bg-neutral-200 rounded flex items-center justify-center mr-2">
                            <PhoneIcon className="w-3 h-3 text-neutral-600" />
                          </div>
                          <h4 className="font-medium text-neutral-900">โทรศัพท์</h4>
                        </div>
                        <div className="space-y-1">
                          <button
                            onClick={() => handleContactClick(
                              'phone',
                              company.phone1,
                              () => window.open(`tel:${company.phone1}`, '_self')
                            )}
                            className="block text-neutral-700 hover:text-primary-600 text-sm text-left"
                          >
                            {formatPhoneNumber(company.phone1)}
                          </button>
                          {company.phone2 && (
                            <button
                              onClick={() => handleContactClick(
                                'phone',
                                company.phone2,
                                () => window.open(`tel:${company.phone2}`, '_self')
                              )}
                              className="block text-neutral-700 hover:text-primary-600 text-sm text-left"
                            >
                              {formatPhoneNumber(company.phone2)}
                            </button>
                          )}
                          {company.phone3 && (
                            <button
                              onClick={() => handleContactClick(
                                'phone',
                                company.phone3,
                                () => window.open(`tel:${company.phone3}`, '_self')
                              )}
                              className="block text-neutral-700 hover:text-primary-600 text-sm text-left"
                            >
                              {formatPhoneNumber(company.phone3)}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {company.email && (
                      <div className="p-4 bg-neutral-50 rounded">
                        <div className="flex items-center mb-2">
                          <div className="w-6 h-6 bg-neutral-200 rounded flex items-center justify-center mr-2">
                            <EnvelopeIcon className="w-3 h-3 text-neutral-600" />
                          </div>
                          <h4 className="font-medium text-neutral-900">อีเมล</h4>
                        </div>
                        <button
                          onClick={() => handleContactClick(
                            'email',
                            company.email,
                            () => window.open(`mailto:${company.email}`, '_self')
                          )}
                          className="text-neutral-700 hover:text-primary-600 text-sm break-all text-left"
                        >
                          {company.email}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 text-center">
                    <div className="flex justify-center mb-3">
                      <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                        <LockClosedIcon className="w-6 h-6 text-yellow-600" />
                      </div>
                    </div>
                    <h4 className="text-base font-semibold text-yellow-900 mb-2">
                      ข้อมูลติดต่อถูกซ่อน
                    </h4>
                    {isGuest ? (
                      <>
                        <p className="text-sm text-yellow-700 mb-4">
                          กรุณาเข้าสู่ระบบเพื่อดูข้อมูลติดต่อบริษัท
                        </p>
                        <Link to="/login">
                          <Button variant="default" size="default">
                            เข้าสู่ระบบ
                          </Button>
                        </Link>
                      </>
                    ) : ownershipStatus?.status === 'pending' ? (
                      <>
                        <p className="text-sm text-yellow-700 mb-4">
                          คำขอ claim ของคุณอยู่ระหว่างรอการอนุมัติ
                        </p>
                        <div className="inline-flex items-center px-4 py-2 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded font-medium">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          รอการอนุมัติ
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-yellow-700 mb-4">
                          คุณต้องเป็นเจ้าของบริษัทนี้เพื่อดูข้อมูลติดต่อ
                        </p>
                        <Button variant="default" size="default" onClick={handleClaimClick}>
                          Claim บริษัทนี้
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {/* Social Media - Show Line & Facebook only if user has permission */}
                {canSeeContact && (company.lineId || company.facebookUrl) && (
                  <div className="p-4 bg-neutral-50 rounded">
                    <h4 className="font-medium text-neutral-900 mb-3">ช่องทางติดต่ออื่น</h4>
                    <div className="flex flex-wrap gap-2">
                      {company.lineId && (
                        <button
                          onClick={() => handleContactClick(
                            'line',
                            company.lineId,
                            () => window.open(`https://line.me/ti/p/${company.lineId}`, '_blank')
                          )}
                          className="flex items-center px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                        >
                          <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.628-.629.628M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                          </svg>
                          Line: {company.lineId}
                        </button>
                      )}
                      {company.facebookUrl && (
                        <button
                          onClick={() => handleContactClick(
                            'facebook',
                            company.facebookUrl,
                            () => window.open(company.facebookUrl, '_blank')
                          )}
                          className="flex items-center px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                          Facebook
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Website - Only visible if user has permission */}
                {canSeeContact && company.website && (
                  <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded">
                    <h4 className="font-medium text-neutral-900 mb-3">เว็บไซต์บริษัท</h4>
                    <button
                      onClick={() => {
                        const normalizedUrl = normalizeWebsiteUrl(company.website)
                        if (normalizedUrl) {
                          handleContactClick(
                            'website',
                            company.website,
                            () => window.open(normalizedUrl, '_blank')
                          )
                        }
                      }}
                      className="flex items-center px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 transition-colors"
                    >
                      <GlobeAltIcon className="w-3 h-3 mr-1" />
                      เยี่ยมชมเว็บไซต์
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* GPS Location - Show if latitude and longitude exist */}
            {company.latitude && company.longitude && (
              <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
                <div className="border-b border-neutral-200 px-6 py-4">
                  <h2 className="text-lg font-semibold text-neutral-900 flex items-center">
                    <MapPinIcon className="w-5 h-5 mr-2 text-blue-600" />
                    ตำแหน่งบนแผนที่
                  </h2>
                </div>
                <div className="p-6 space-y-4">
                  {/* GPS Coordinates */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">พิกัด GPS:</span>
                      <span className="text-sm font-mono text-gray-900">
                        {company.latitude.toFixed(6)}, {company.longitude.toFixed(6)}
                      </span>
                    </div>
                  </div>

                  {/* Static Map */}
                  <StaticMap
                    latitude={company.latitude}
                    longitude={company.longitude}
                    height="350px"
                    zoom={15}
                    showLink={true}
                  />

                  {/* Additional Actions */}
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${company.latitude},${company.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      เปิดใน Google Maps
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${company.latitude}, ${company.longitude}`)
                        alert('คัดลอกพิกัดเรียบร้อย!')
                      }}
                      className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      คัดลอกพิกัด
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    ใช้พิกัด GPS เพื่อนำทางไปยังสำนักงานของบริษัท
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Company Statistics */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
              <div className="border-b border-neutral-200 px-4 py-3">
                <h3 className="text-lg font-semibold text-neutral-900">สถิติบริษัท</h3>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between p-2 bg-neutral-50 rounded">
                  <span className="text-neutral-600 text-sm">ผู้เข้าชม</span>
                  <span className="font-semibold text-neutral-900">{company.viewCount.toLocaleString()}</span>
                </div>

                <div className="flex items-center justify-between p-2 bg-neutral-50 rounded">
                  <span className="text-neutral-600 text-sm">การติดต่อ</span>
                  <span className="font-semibold text-neutral-900">{company.contactCount.toLocaleString()}</span>
                </div>

                {company.establishedYear && (
                  <div className="flex items-center justify-between p-2 bg-neutral-50 rounded">
                    <span className="text-neutral-600 text-sm">ปีที่ก่อตั้ง</span>
                    <span className="font-semibold text-neutral-900">{company.establishedYear}</span>
                  </div>
                )}

                {company.licenseNumber && (
                  <div className="p-2 bg-neutral-50 rounded">
                    <div className="text-neutral-600 text-sm mb-1">เลขใบอนุญาต</div>
                    <p className="text-neutral-900 text-sm font-mono bg-white p-2 rounded border">
                      {company.licenseNumber}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
              <div className="border-b border-neutral-200 px-4 py-3">
                <h3 className="text-lg font-semibold text-neutral-900">การดำเนินการ</h3>
              </div>
              <div className="p-4 space-y-3">
                <button
                  onClick={() => navigator.share?.({ title: company.nameTh, text: company.description, url: window.location.href })}
                  className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium text-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  แชร์บริษัทนี้
                </button>

                <button
                  onClick={() => {
                    const subject = `รายงานข้อมูลผิดพลาด - ${company.nameTh}`
                    const body = `เรียน ทีมงาน ไทยเซ็กคิวริตี้ไดเรกทอรี่

ข้าพเจ้าต้องการรายงานข้อมูลผิดพลาดของบริษัท: ${company.nameTh}
URL: ${window.location.href}

รายละเอียดข้อมูลที่ผิดพลาด:
[โปรดระบุข้อมูลที่ผิดพลาดและข้อมูลที่ถูกต้อง]

ขอบคุณครับ/ค่ะ`
                    const mailtoUrl = `mailto:info@koder3.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
                    window.open(mailtoUrl, '_self')
                  }}
                  className="w-full flex items-center justify-center px-4 py-2 bg-neutral-200 hover:bg-neutral-300 text-neutral-700 rounded font-medium text-sm transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  รายงานข้อมูลผิดพลาด
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CompanyDetailPage